/**
 * RefundOrderSubview — wallet-portal Orders drawer refund step.
 *
 * Renders IN-PLACE inside the Order-details drawer (view_order/index.tsx swaps
 * this in for the normal drawer content while a refund is in progress) — same
 * step-transition pattern as ReportProblemSubview in view_transaction. `onBack`
 * returns to the Order details view within the same drawer; `onClose` dismisses
 * the whole drawer, matching the drawer's own close button.
 *
 * Pixel-perfect to the Figma spec (r16, gap 24 between blocks).
 *
 * MONEY-PATH rules:
 *  - idempotency_key generated ONCE per subview-mount; reused on submit so
 *    a double-click / network retry cannot issue a second refund.
 *  - Amount validated ≤ effectiveAvailable — the backend-quoted
 *    `available_to_refund` once the quote has loaded, else the
 *    `availableToRefund` prop — so a stale/incomplete parent-supplied cap can
 *    never let the merchant request more than the backend currently confirms
 *    is refundable.
 *  - Button disabled while any quote fetch or submit is in flight.
 *  - On error the subview stays open; error message is shown inline.
 *  - apiRefundService / REFUND_REASONS imported from shared locations —
 *    no duplication of the refund flow that already exists in the dev-portal.
 */

import { FC, useEffect, useRef, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import { apiRefundService, RefundApiError } from '@api';
import FlagIcon from '@components/common/flag_icon';
import Info from '@components/common/info';
import InfoBlock from '@components/common/info_block';
import { Icon } from '@components/icons';
import { getLoyaltyLevelString, prettifyAmount } from '@utils/index';
import { formatFeeRate } from '@utils/payment';
import { useUserContext } from '@hooks/context/useUserContext';
import { REFUND_REASONS, TRefundReason } from '@const/orders';
import styles from './refund_subview.module.scss';

// ─── Types ───────────────────────────────────────────────────────────────────

// The refund quote also carries the fee's rate structure (fee_flat / fee_percent),
// used to caption the fee as "XEPPT fee (2% + 2.5 CAD)". These are newer than the
// bundled SDK's return type, so they're declared alongside it here.
type TRefundQuote = Awaited<ReturnType<typeof apiRefundService.getRefundQuote>> & {
    fee_flat?: number;
    fee_percent?: number;
};
type TRefundLimitPeriod = TRefundQuote['limits']['daily'];

export interface RefundOrderSubviewProps {
    orderId: string;
    availableToRefund: number;
    currency: string;
    /** Return to the Order details view within the same drawer. */
    onBack: () => void;
    /** Dismiss the entire Order details drawer. */
    onClose: () => void;
    /** Called immediately after refundOrder resolves — caller should
     *  re-fetch the order and show a success toast. */
    onSuccess: () => void;
}

// ─── Currency → ISO-3166-1 flag code ─────────────────────────────────────────

const CURRENCY_FLAG: Record<string, string> = {
    CAD: 'CA',
    USD: 'US',
    EUR: 'EU',
    GBP: 'GB',
    AUD: 'AU',
    CHF: 'CH',
    JPY: 'JP',
    MXN: 'MX'
};

// A period only counts as "configured" once it actually has a cap set — an
// absent period, a null/undefined cap, and an explicit cap of 0 all mean "no
// limit" and are dropped (row-level, and whole-block once none remain) rather
// than shown. `!= null` catches both null and undefined without misreading a
// real cap of 0 as configured.
function isConfiguredLimit(
    period: TRefundLimitPeriod | null | undefined
): period is TRefundLimitPeriod {
    return period?.cap != null && period.cap !== 0;
}

// ─── Component ───────────────────────────────────────────────────────────────

const RefundOrderSubview: FC<RefundOrderSubviewProps> = ({
    orderId,
    availableToRefund,
    currency,
    onBack,
    onClose,
    onSuccess
}) => {
    const { loyalty } = useUserContext();
    const [quote, setQuote] = useState<TRefundQuote | null>(null);
    // Backend-quoted `available_to_refund` is the authoritative cap once it
    // has loaded — it governs even if the `availableToRefund` prop (sourced
    // from the parent's own order snapshot) is stale or was computed from an
    // incomplete order. Defense-in-depth: a money-surface cap must never rely
    // on a single upstream source alone. Before the quote resolves, `quote` is
    // still null, so this is simply `availableToRefund`.
    const effectiveAvailable = quote?.available_to_refund ?? availableToRefund;
    // Pre-fill with the full remaining / available amount.
    const [amount, setAmount] = useState(
        effectiveAvailable > 0 ? String(effectiveAvailable) : ''
    );
    const [debouncedAmount] = useDebounceValue(amount, 400);
    const [reason, setReason] = useState<TRefundReason | ''>('');
    // Free-text reason typed by the user when "Other" is selected — this is
    // what actually gets submitted as `reason` in that case, not the literal
    // string "Other".
    const [customReason, setCustomReason] = useState('');
    const [isReasonOpen, setIsReasonOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isQuoting, setIsQuoting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    // Mirrors view_transaction's showFees step: "Get info" swaps this subview's
    // own content for a fee-breakdown screen in place, rather than an inline
    // tooltip — the proven pattern, and inherently immune to edge-clipping
    // since it takes over the whole content area instead of popping out of it.
    const [showFeesInfo, setShowFeesInfo] = useState(false);

    // One idempotency key per subview-mount — prevents double-refund on double-click.
    const idempotencyKeyRef = useRef(crypto.randomUUID());
    const reasonRef = useRef<HTMLDivElement>(null);

    const amountNum = parseFloat(amount);
    const exceedsAvailable = !isNaN(amountNum) && amountNum > effectiveAvailable;
    const isAmountValid = !isNaN(amountNum) && amountNum > 0 && !exceedsAvailable;
    const isOtherReasonValid = reason !== 'Other' || customReason.trim() !== '';
    const isFormValid = isAmountValid && reason !== '' && isOtherReasonValid;
    const canSubmit = isFormValid && !isSubmitting && !isQuoting;

    const flagCode = CURRENCY_FLAG[currency.toUpperCase()] ?? null;

    // Derived display flags — degrade gracefully when the quote omits a field.
    const showFees = !!quote && quote.estimated_fee > 0;
    const showTotal = !!quote;
    const showPoints = !!quote && quote.points > 0;

    // Fee rate structure ("2% + 2.5 CAD") for the breakdown caption — the same
    // shared formatter the send-money Estimated fees screen uses. Empty when the
    // plan has no fee, so the caption falls back to a plain "XEPPT fee".
    const feeRate = formatFeeRate({
        percent: quote?.fee_percent,
        flat: quote?.fee_flat,
        currency
    });
    const feeLevelString = getLoyaltyLevelString(loyalty?.level);

    // ── Close reason dropdown when clicking outside it ──────────────────────
    useEffect(() => {
        if (!isReasonOpen) return;
        const handler = (e: MouseEvent) => {
            if (
                reasonRef.current &&
                !reasonRef.current.contains(e.target as Node)
            ) {
                setIsReasonOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isReasonOpen]);

    // ── Fetch a new quote whenever the debounced amount changes ─────────────
    useEffect(() => {
        const num = parseFloat(debouncedAmount);
        if (isNaN(num) || num <= 0) {
            setQuote(null);
            return;
        }
        let cancelled = false;
        setIsQuoting(true);
        void apiRefundService
            .getRefundQuote(orderId, num)
            .then((q) => {
                if (!cancelled) setQuote(q);
            })
            .catch(() => {
                if (!cancelled) setQuote(null);
            })
            .finally(() => {
                if (!cancelled) setIsQuoting(false);
            });
        return () => {
            cancelled = true;
        };
    }, [debouncedAmount, orderId]);

    // ── Clamp the displayed amount down if the quote resolves lower ─────────
    // A concurrent partial refund can land between modal-open (the
    // `availableToRefund` prop) and this quote resolving. Submission is
    // already blocked either way (exceedsAvailable/canSubmit use
    // effectiveAvailable), but leaving the input showing a stale, too-high
    // figure is a money-surface UX inconsistency once the banner has already
    // moved to the lower, authoritative amount. Never clamp upward — a
    // smaller amount the user is actively typing must never be overwritten.
    // Uses the functional setAmount form (reads `prev`, not the outer
    // `amount`) so this only needs to depend on `quote` — the re-fetched
    // quote for the newly-clamped amount returns the same available_to_refund
    // (it reflects the order's remaining balance, not the requested amount),
    // so this converges without looping.
    useEffect(() => {
        if (!quote) return;
        const authoritativeAvailable = quote.available_to_refund;
        setAmount((prev) => {
            const current = parseFloat(prev);
            if (isNaN(current) || current <= authoritativeAvailable) return prev;
            return authoritativeAvailable > 0 ? String(authoritativeAvailable) : '';
        });
    }, [quote]);

    // ── Submit ──────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!canSubmit) return;
        setIsSubmitting(true);
        setErrorMsg('');
        // "Other" sends the user's typed text, never the literal word "Other".
        const resolvedReason = reason === 'Other' ? customReason.trim() : reason;
        try {
            await apiRefundService.refundOrder(orderId, {
                reason: resolvedReason,
                amount: amountNum,
                idempotency_key: idempotencyKeyRef.current
            });
        } catch (err) {
            const msg =
                err instanceof RefundApiError
                    ? err.message || 'Refund failed. Please try again.'
                    : 'Refund failed. Please try again.';
            setErrorMsg(msg);
            setIsSubmitting(false);
            return;
        }
        // Refund succeeded — hand off to the parent (returns to Order details
        // and re-fetches the order). Guard the handoff so a throw here can't
        // leave the button stuck in the submitting state.
        try {
            onSuccess();
        } catch {
            setIsSubmitting(false);
        }
    };

    // Only rows with an actually configured cap are kept — an absent or
    // zero-cap period ("No limit") is dropped entirely, and the whole card
    // is skipped once none remain.
    const rawLimitsRows: { label: string; period: TRefundLimitPeriod | null }[] = [
        { label: 'Daily', period: quote?.limits?.daily ?? null },
        { label: 'Weekly', period: quote?.limits?.weekly ?? null },
        { label: 'Monthly', period: quote?.limits?.monthly ?? null }
    ];
    const limitsRows = rawLimitsRows.filter(
        (row): row is { label: string; period: TRefundLimitPeriod } =>
            isConfiguredLimit(row.period)
    );
    const hasLimits = limitsRows.length > 0;

    // Mirrors view_transaction's showFees step exactly: the fee "Get info"
    // affordance swaps this subview's whole content for a dedicated
    // fee-breakdown screen (own header, own back/close), rather than an
    // inline tooltip — placed after every hook above, per Rules of Hooks.
    if (showFeesInfo) {
        return (
            <div className={styles.rm_fees_subview}>
                <div className={styles.rm_fees_subview_header}>
                    <button
                        type="button"
                        className={styles.rm_fees_back_btn}
                        onClick={() => setShowFeesInfo(false)}
                        aria-label="Back to refund">
                        <Icon name="arrow_left" width={20} height={20} />
                    </button>
                    <p className={styles.rm_fees_subview_title}>Estimated fees</p>
                    <button
                        type="button"
                        className={styles.rm_fees_close_btn}
                        onClick={onClose}
                        aria-label="Close">
                        <Icon name="close" width={20} height={20} />
                    </button>
                </div>
                <div className={styles.rm_fees_rows}>
                    <div className={styles.rm_fees_item}>
                        <p>{feeRate ? `XEPPT fee (${feeRate})` : 'XEPPT fee'}</p>
                        <p className={styles.rm_fees_row_value}>
                            ${prettifyAmount(Number(quote?.estimated_fee ?? 0).toFixed(2))}{' '}
                            {currency}
                        </p>
                    </div>
                    <hr className={styles.rm_fees_divider} />
                    <div className={styles.rm_fees_item}>
                        <p>
                            <b>Total you pay</b>
                        </p>
                        <p className={styles.rm_fees_row_value}>
                            <b>
                                ${prettifyAmount(Number(quote?.total ?? 0).toFixed(2))} {currency}
                            </b>
                        </p>
                    </div>
                </div>
                <p className={styles.rm_fees_subtext}>Included in the total charged</p>
                <InfoBlock variant="gray">
                    {feeRate
                        ? `Refund fees for ${feeLevelString}-level users are ${feeRate}, included in the total charged.`
                        : 'Processing fee for this refund, calculated based on your current pricing plan.'}
                </InfoBlock>
            </div>
        );
    }

    return (
        <div className={styles.rm_modal}>
            {/* Header: back arrow returns to Order details, X closes the whole drawer */}
            <div className={styles.rm_header}>
                <button
                    type="button"
                    className={styles.rm_back}
                    onClick={onBack}
                    aria-label="Back to order details">
                    <Icon name="arrow_left" width={24} height={24} />
                </button>
                <p className={styles.rm_title}>Refund</p>
                <button
                    type="button"
                    className={styles.rm_close}
                    onClick={onClose}
                    aria-label="Close">
                    <Icon name="close" width={24} height={24} />
                </button>
            </div>
            <div className={styles.rm_divider} />

            {/* ── Content blocks ───────────────────────────────────── */}
            <div className={styles.rm_content}>

                {/* Block 1: Limits — visible only once at least one period has a configured cap */}
                {hasLimits && (
                    <div className={styles.rm_limits_card}>
                        <p className={styles.rm_limits_heading}>Limits</p>
                        <div className={styles.rm_limits_rows}>
                            {isQuoting && !quote ? (
                                <p className={styles.rm_loading_text}>Loading…</p>
                            ) : (
                                limitsRows.map(({ label, period }) => (
                                    <div key={label} className={styles.rm_limit_row}>
                                        <span className={styles.rm_limit_label}>
                                            {label}
                                        </span>
                                        <div className={styles.rm_limit_values}>
                                            <span className={styles.rm_limit_used}>
                                                {prettifyAmount(
                                                    Number(period.used).toFixed(2)
                                                )}{' '}
                                                {currency}
                                            </span>
                                            <span className={styles.rm_limit_sep}>
                                                /
                                            </span>
                                            <span className={styles.rm_limit_cap}>
                                                {prettifyAmount(
                                                    Number(period.cap).toFixed(2)
                                                )}{' '}
                                                {currency}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {/* Block 2: Available-to-refund banner — always visible */}
                <div className={styles.rm_available_banner}>
                    <span className={styles.rm_available_label}>
                        Available to refund:
                    </span>
                    <span className={styles.rm_available_value}>
                        ${prettifyAmount(Number(effectiveAvailable).toFixed(2))}{' '}
                        {currency}
                    </span>
                </div>

                {/* Block 3: Amount input + fees/points below it */}
                <div className={styles.rm_field_group}>
                    <div className={styles.rm_field_label_row}>
                        <span className={styles.rm_field_label}>Refund amount</span>
                        <Info variant="right" content={<>Enter the amount to refund. Cannot exceed the available balance.</>} />
                    </div>
                    <div
                        className={[
                            styles.rm_amount_input_box,
                            exceedsAvailable ? styles.rm_amount_input_error : ''
                        ]
                            .join(' ')
                            .trim()}>
                        <input
                            type="number"
                            min="0"
                            max={effectiveAvailable}
                            step="0.01"
                            className={styles.rm_amount_input}
                            value={amount}
                            placeholder="0.00"
                            onChange={(e) => setAmount(e.target.value)}
                            aria-label="Refund amount"
                            aria-invalid={exceedsAvailable}
                        />
                        <div className={styles.rm_currency_suffix}>
                            {flagCode && (
                                <FlagIcon name={flagCode} width={24} height={24} />
                            )}
                            <span>{currency}</span>
                        </div>
                    </div>
                    {exceedsAvailable && (
                        <p className={styles.rm_amount_error_text}>
                            Amount exceeds available balance
                        </p>
                    )}

                    {/* Block 4: Fees/total — only when quote loaded with data */}
                    {(showFees || showTotal) && quote && (
                        <div className={styles.rm_fees_block}>
                            {showFees && (
                                <div className={styles.rm_fee_row}>
                                    <span className={styles.rm_fee_label}>
                                        Estimated fees:
                                    </span>
                                    <span className={styles.rm_fee_value}>
                                        ${prettifyAmount(
                                            Number(quote.estimated_fee).toFixed(2)
                                        )}{' '}
                                        {currency}
                                    </span>
                                    {/* Mirrors view_transaction's fee "Get info": swaps
                                        this subview's content for a fee-breakdown
                                        screen in place, not an inline tooltip. */}
                                    <button
                                        type="button"
                                        className={styles.rm_get_info_btn}
                                        onClick={() => setShowFeesInfo(true)}>
                                        <Icon name="info" width={18} height={18} />
                                        Get info
                                    </button>
                                </div>
                            )}
                            {showTotal && (
                                <div className={styles.rm_total_row}>
                                    <span className={styles.rm_total_label}>
                                        Total you pay:
                                    </span>
                                    <span className={styles.rm_total_value}>
                                        ${prettifyAmount(
                                            Number(quote.total).toFixed(2)
                                        )}{' '}
                                        {currency}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Points banner */}
                    {showPoints && quote && (
                        <div className={styles.rm_points_banner}>
                            <Icon
                                name="loyalty"
                                width={24}
                                height={24}
                                className={styles.rm_points_icon}
                            />
                            <span className={styles.rm_points_text}>
                                On this transaction you will earn{' '}
                                <strong className={styles.rm_points_value}>
                                    {quote.points} points
                                </strong>
                                {/* Matches view_transaction's points Info exactly: variant="top". */}
                                <Info variant="top" className={styles.rm_points_info_trigger} content={<>Points are awarded on every successful refund transaction.</>} />
                            </span>
                        </div>
                    )}
                </div>

                {/* Block 5: Reason dropdown (+ free-text field when "Other" is selected) */}
                <div className={styles.rm_field_group}>
                    <div className={styles.rm_field_label_row}>
                        <span className={styles.rm_field_label}>Refund reason</span>
                        <Info variant="right" content={<>Select the reason for this refund. This is recorded for your records and may be shared with the customer.</>} />
                    </div>
                    <div className={styles.rm_reason_wrapper} ref={reasonRef}>
                        <button
                            type="button"
                            className={[
                                styles.rm_reason_trigger,
                                isReasonOpen ? styles.rm_reason_open : ''
                            ]
                                .join(' ')
                                .trim()}
                            onClick={() => setIsReasonOpen((prev) => !prev)}
                            aria-haspopup="listbox"
                            aria-expanded={isReasonOpen}>
                            <span
                                className={
                                    reason
                                        ? styles.rm_reason_selected_value
                                        : styles.rm_reason_placeholder
                                }>
                                {reason || 'Select a reason'}
                            </span>
                            <Icon
                                name={isReasonOpen ? 'arrow_up' : 'arrow_down'}
                                width={24}
                                height={24}
                            />
                        </button>
                        {isReasonOpen && (
                            <div
                                className={styles.rm_reason_dropdown}
                                role="listbox">
                                {REFUND_REASONS.map((r) => (
                                    <div
                                        key={r}
                                        role="option"
                                        aria-selected={reason === r}
                                        className={[
                                            styles.rm_reason_option,
                                            reason === r
                                                ? styles.rm_reason_option_active
                                                : ''
                                        ]
                                            .join(' ')
                                            .trim()}
                                        tabIndex={0}
                                        onClick={() => {
                                            setReason(r);
                                            setIsReasonOpen(false);
                                        }}
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === 'Enter' ||
                                                e.key === ' '
                                            ) {
                                                setReason(r);
                                                setIsReasonOpen(false);
                                            }
                                        }}>
                                        {r}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {reason === 'Other' && (
                        <input
                            type="text"
                            className={styles.rm_custom_reason_input}
                            value={customReason}
                            placeholder="Describe the reason"
                            onChange={(e) => setCustomReason(e.target.value)}
                            aria-label="Custom refund reason"
                        />
                    )}
                </div>

                {/* Inline error */}
                {errorMsg && (
                    <p className={styles.rm_error_msg} role="alert">
                        {errorMsg}
                    </p>
                )}
            </div>

            {/* ── Footer ───────────────────────────────────────────── */}
            <div className={styles.rm_footer}>
                <button
                    type="button"
                    className={styles.rm_btn_cancel}
                    onClick={onBack}>
                    Cancel
                </button>
                <button
                    type="button"
                    className={styles.rm_btn_refund}
                    disabled={!canSubmit}
                    onClick={handleSubmit}
                    aria-busy={isSubmitting}>
                    {isSubmitting ? 'Processing…' : 'Refund'}
                </button>
            </div>
        </div>
    );
};

export default RefundOrderSubview;
